/**
 * 购物车贝塞尔曲线动画工具
 */

export interface Point {
  x: number;
  y: number;
}

export interface BezierPoint {
  start: Point;
  end: Point;
  control1?: Point;
  control2?: Point;
}

export const NAV_CART_ID = 'nav-cart-btn';

/**
 * 计算贝塞尔曲线路径上的点
 */
export function getBezierPoint(
  p0: Point,
  p1: Point,
  p2: Point,
  p3: Point,
  t: number,
): Point {
  const x =
    Math.pow(1 - t, 3) * p0.x +
    3 * Math.pow(1 - t, 2) * t * p1.x +
    3 * (1 - t) * Math.pow(t, 2) * p2.x +
    Math.pow(t, 3) * p3.x;

  const y =
    Math.pow(1 - t, 3) * p0.y +
    3 * Math.pow(1 - t, 2) * t * p1.y +
    3 * (1 - t) * Math.pow(t, 2) * p2.y +
    Math.pow(t, 3) * p3.y;

  return { x, y };
}

/**
 * 生成贝塞尔曲线动画路径
 */
export function createBezierAnimation(
  start: Point,
  end: Point,
  duration: number = 600,
): Promise<void> {
  return new Promise((resolve, reject) => {
    // 创建动画元素
    const animationEl = document.createElement('div');
    animationEl.className = 'cart-animation';
    animationEl.style.cssText = `
      position: fixed;
      width: 24px;
      height: 24px;
      background: #3284FF;
      border-radius: 50%;
      pointer-events: none;
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 12px rgba(50, 132, 255, 0.4);
      transform: translate(-50%, -50%);
      will-change: transform, opacity;
    `;

    // 添加购物车图标
    animationEl.innerHTML = `
      <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
        <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12L8.1 13h7.45c.75 0 1.41-.41 1.75-1.03L21.7 4H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"/>
      </svg>
    `;

    // 确保元素被添加到DOM中
    document.body.appendChild(animationEl);

    // 计算控制点，创建更明显的贝塞尔曲线弧度
    const distanceX = Math.abs(end.x - start.x);
    const distanceY = Math.abs(end.y - start.y);
    const arcHeight = Math.max(distanceX * 0.3, distanceY * 0.5, 80); // 动态计算弧度高度

    const control1 = {
      x: start.x + (end.x - start.x) * 0.25,
      y: start.y - arcHeight, // 向上弯曲更多
    };
    const control2 = {
      x: start.x + (end.x - start.x) * 0.75,
      y: end.y - arcHeight * 0.3, // 在终点前就开始下降
    };

    const startTime = Date.now();
    function animate() {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // 使用更自然的缓动函数
      const easeProgress = easeOutBack(progress);

      const point = getBezierPoint(
        start,
        control1,
        control2,
        end,
        easeProgress,
      );

      animationEl.style.left = point.x + 'px';
      animationEl.style.top = point.y + 'px';
      animationEl.style.opacity = (1 - progress * 0.3).toString();
      animationEl.style.transform = `translate(-50%, -50%) scale(${1 - progress * 0.1})`;

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // 动画完成，确保在终点显示一小段时间
        animationEl.style.left = end.x + 'px';
        animationEl.style.top = end.y + 'px';
        animationEl.style.opacity = '0.7';

        // 添加购物车抖动效果
        setTimeout(() => {
          const cartIcon = document.querySelector(
            `#${NAV_CART_ID}`,
          ) as HTMLElement;
          if (cartIcon) {
            cartIcon.style.animation = 'cart-bounce 0.6s ease-in-out';
            setTimeout(() => {
              cartIcon.style.animation = '';
            }, 600);
          }

          // 移除动画元素
          animationEl.remove();
          resolve();
        }, 100);
      }
    }

    // 设置初始位置
    animationEl.style.left = start.x + 'px';
    animationEl.style.top = start.y + 'px';

    // 启动动画
    try {
      requestAnimationFrame(animate);
    } catch (error) {
      // 如果动画启动失败，清理资源
      animationEl.remove();
      reject(error);
    }

    // 设置超时保护，防止动画永远不完成
    setTimeout(
      () => {
        if (animationEl.parentNode) {
          animationEl.remove();
          resolve(); // 超时后也认为完成，避免Promise永远pending
        }
      },
      Math.max(duration * 2, 2000),
    );
  });
}

/**
 * 更自然的缓动函数，带有轻微的回弹效果
 */
function easeOutBack(t: number): number {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
}

/**
 * 获取元素相对于视窗的中心点坐标
 */
export function getElementCenter(element: Element): Point {
  const rect = element.getBoundingClientRect();
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
  };
}

/**
 * 获取购物车图标的位置
 */
export function getCartPosition(): Point | null {
  // 使用更精确的选择器来找到购物车图标
  const cartIcon = document.querySelector(`#${NAV_CART_ID}`) as HTMLElement;
  if (!cartIcon) return null;

  const rect = cartIcon.getBoundingClientRect();
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
  };
}

/**
 * 启动购物车动画
 */
export async function startCartAnimation(
  sourceElement: Element,
): Promise<void> {
  const start = getElementCenter(sourceElement);
  const end = getCartPosition();

  if (!end) {
    console.warn('购物车图标未找到');
    return;
  }

  try {
    await createBezierAnimation(start, end, 1000);
  } catch (error) {
    console.error('购物车动画执行失败:', error);
  }
}
